/**
 * Amplitude analytics for the web app.
 * API key: use REACT_APP_AMPLITUDE_API_KEY (set via server secrets for staging/production).
 */
import * as amplitude from '@amplitude/analytics-browser';
import { SessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { Role } from '../../config/acl/roles/Role';
import { groupCharterer, groupFreighter } from '../../config/acl/roles/groups';

const API_KEY = import.meta.env.REACT_APP_AMPLITUDE_API_KEY as string | undefined;

export type OrgType = 'broker' | 'operator' | 'admin';

let isInitialized = false;

function getOrgTypeFromRole(role: Role): OrgType | undefined {
  if (role === Role.admin) return 'admin';
  if (groupCharterer.includes(role)) return 'broker';
  if (groupFreighter.includes(role)) return 'operator';
  return undefined;
}

/**
 * Initialize Amplitude. Call once at app startup.
 * Uses REACT_APP_AMPLITUDE_API_KEY (prefer server secrets for staging/prod).
 */
export async function initAmplitude(): Promise<void> {
  if (typeof window === 'undefined' || !API_KEY) {
    if (import.meta.env.DEV) {
      console.log('[Amplitude] Skipped (no API key or SSR)');
    }
    return;
  }
  if (isInitialized) return;

  try {
    amplitude.init(API_KEY, undefined, {
      minIdLength: 1, // Allow numeric user/org IDs (e.g. "1", "42"); Amplitude default is 5
      defaultTracking: {
        sessions: true,
        pageViews: true,
        formInteractions: false,
        fileDownloads: false,
      },
    });

    // Mark initialized immediately so setUserId/identify work when auth completes (refreshToken
    // or login can run before the plugin is added; otherwise events would stay anonymous).
    isInitialized = true;
    if (import.meta.env.DEV) {
      console.log('[Amplitude] Initialized');
    }

    const sessionReplayPlugin = new SessionReplayPlugin({
      sampleRate: import.meta.env.PROD ? 0.1 : 0,
    });
    await amplitude.add(sessionReplayPlugin).promise;
  } catch (e) {
    console.error('[Amplitude] Init failed', e);
  }
}

/**
 * Set user identity and group. Call after login when user and company are available.
 */
export function setAmplitudeUser(props: {
  userId: string | number;
  userEmail?: string;
  userRoleInOrg: string;
  orgType: OrgType;
  orgId: string | number;
  orgName?: string;
}): void {
  if (!isInitialized) return;

  try {
    const userId = String(props.userId);
    amplitude.setUserId(userId);

    const identify = new amplitude.Identify();
    identify.set('user_email', props.userEmail ?? undefined);
    identify.set('user_role_in_org', props.userRoleInOrg);
    identify.set('org_type', props.orgType);
    identify.set('org_id', String(props.orgId));
    identify.set('org_name', props.orgName ?? undefined);
    amplitude.identify(identify);

    amplitude.setGroup('org', String(props.orgId));
    if (import.meta.env.DEV) {
      console.log('[Amplitude] User set', { userId, orgType: props.orgType });
    }
  } catch (e) {
    console.error('[Amplitude] setUser failed', e);
  }
}

/**
 * Clear user (e.g. on logout).
 */
export function resetAmplitudeUser(): void {
  if (!isInitialized) return;
  try {
    amplitude.reset();
  } catch (e) {
    console.error('[Amplitude] reset failed', e);
  }
}

/**
 * Track a generic event with optional properties.
 */
export function track(eventName: string, eventProperties?: Record<string, unknown>): void {
  if (!isInitialized) {
    if (import.meta.env.DEV) {
      console.log('[Amplitude] Event (no init):', eventName, eventProperties);
    }
    return;
  }
  try {
    amplitude.track(eventName, eventProperties);
  } catch (e) {
    console.error('[Amplitude] track failed', eventName, e);
  }
}

// ——— UI events ———

export function trackButtonClicked(buttonName: string, properties?: Record<string, unknown>): void {
  track('Button Clicked', { buttonName, ...properties });
}

export function trackDialogOpened(dialogName: string, properties?: Record<string, unknown>): void {
  track('Dialog Opened', { dialogName, ...properties });
}

export function trackMessageSeen(messageId: string, properties?: Record<string, unknown>): void {
  track('Message Seen', { messageId, ...properties });
}

// ——— Business events (aligned with metrics/goals + your list) ———

export const amplitudeEvents = {
  registered: () => track('Registered', {}),
  approved: (context?: string) => track('Approved', context ? { context } : {}),
  planeAdded: () => track('Plane Added', {}),
  planeChanged: (properties?: Record<string, unknown>) => track('Plane Changed', properties),
  calculatorUsed: (properties?: Record<string, unknown>) => track('Calculator Used', properties),
  newSchedule: (properties?: Record<string, unknown>) => track('New Schedule', properties),
  eventCreated: (properties?: Record<string, unknown>) => track('Event Created', properties),
  searchFlight: (properties?: Record<string, unknown>) => track('Search Flight', properties),
  /** When broker clicks "Enquire a charter" with selected planes. Arrays are parallel (same index = same plane). */
  enquireCharter: (properties?: {
    planes_selected_count?: number;
    plane_numbers?: string[];
    prices?: number[];
    positioning_minutes?: (number | undefined)[];
    flight_time_minutes?: (number | undefined)[];
  }) => track('Enquire Charter', properties),

  /** Operator approved (submitted offer) or declined (archived) a charter request. */
  operatorRespondToRequest: (properties?: {
    action: 'approve' | 'decline';
    proposed_count: number;
    declined_count: number;
    itinerary?: Array<{ airport_from_code?: string; airport_to_code?: string }>;
    min_proposed_price?: number | null;
    max_proposed_price?: number | null;
  }) => track('Operator Respond To Request', properties),

  visitAgentsPage: () => track('Visit Agents Page', {}),
  openRegisterPageAfterVisitHome: () => track('Open Register Page After Visit Home', {}),
  registrationSuccess: () => track('Registration Success', {}),
  operatorRegistered: () => track('Operator Registered', {}),
  brokerRegistered: () => track('Broker Registered', {}),
  operatorLoggedIn: () => track('Operator Logged In', {}),
  brokerLoggedIn: () => track('Broker Logged In', {}),
  planeCreated: () => track('Plane Created', {}),
  registerFormTouched: () => track('Register Form Touched', {}),
  registerFormRequestSent: () => track('Register Form Request Sent', {}),
  orderRequestMakeOffer: () => track('Order Request Make Offer', {}),
  orderCreated: () => track('Order Created', {}),
  searchEmptyLegs: () => track('Search Empty Legs', {}),
  createEmptyLegOrder: () => track('Create Empty Leg Order', {}),
};

export { getOrgTypeFromRole };
